import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form';

import { AutocompleteActions } from '@components/AutocompleteActions';
import Autocomplete, { AutocompleteProps, AutocompleteValue } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { handleAutocompleteOnScroll } from '@utils/input-utils/handleAutocompleteOnScroll';

// ----------------------------------------------------------------------

interface RHFAutocompleteProps<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
  TFieldValues extends FieldValues = FieldValues
> extends Omit<
    AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
    'renderInput' | 'onChange'
  > {
  name: Path<TFieldValues>;
  label?: string;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  hasMore?: () => boolean;
  loadMore?: () => void;
  customEndAdornment?: React.ReactNode;
  /** Renders a `+` icon in the endAdornment. */
  onCreate?: () => void;
  /** Renders an edit icon in the endAdornment — only when the field has a selected value. */
  onEdit?: () => void;
  /** Tooltip / aria-label for the create button. Defaults to "Create new". */
  createTooltip?: string;
  /** Tooltip / aria-label for the edit button. Defaults to "Edit selected". */
  editTooltip?: string;
  /** Disables only the create button (e.g. permission gating). Defaults to the field's `disabled`. */
  createDisabled?: boolean;
  /** Disables only the edit button (e.g. permission gating). Defaults to the field's `disabled`. */
  editDisabled?: boolean;
  /** Fires after the field value is committed — e.g. to clear a dependent field. */
  onValueChange?: (value: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>) => void;
}

export function RHFAutocomplete<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
  TFieldValues extends FieldValues = FieldValues
>({
  name,
  isOptionEqualToValue,
  label,
  helperText,
  placeholder,
  options,
  required,
  hasMore,
  loadMore,
  loading,
  customEndAdornment,
  onCreate,
  onEdit,
  createTooltip,
  editTooltip,
  createDisabled,
  editDisabled,
  onInputChange,
  onValueChange,
  ...other
}: RHFAutocompleteProps<T, Multiple, DisableClearable, FreeSolo, TFieldValues>) {
  const { control, setValue } = useFormContext<TFieldValues>();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          {...field}
          // By default compare option/value by their `value` property when both are objects.
          // This avoids issues where options are recreated on each render (different references)
          // and prevents duplicate selections for multi-select autocompletes.
          isOptionEqualToValue={
            isOptionEqualToValue ??
            ((option: any, value: any) => {
              const opt = option == null ? option : option?.value ?? option;
              const val = value == null ? value : value?.value ?? value;
              return opt === val;
            })
          }
          loading={loading}
          options={options}
          renderInput={(params) => (
            <TextField
              {...params}
              error={!!error}
              helperText={error ? error?.message : helperText}
              label={label}
              placeholder={placeholder}
              required={required}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {customEndAdornment}
                      {(onCreate || onEdit) && (
                        <AutocompleteActions
                          createDisabled={createDisabled}
                          createTooltip={createTooltip}
                          editDisabled={editDisabled}
                          editTooltip={editTooltip}
                          hasValue={
                            Array.isArray(field.value)
                              ? field.value.length > 0
                              : field.value != null
                          }
                          onCreate={onCreate}
                          onEdit={onEdit}
                        />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          slotProps={{
            listbox: {
              onScroll: (event) => handleAutocompleteOnScroll(event, hasMore, loadMore, loading),
            },
          }}
          onChange={(_, newValue) => {
            setValue(name, newValue as any, { shouldValidate: true, shouldDirty: true });
            // Explicitly reset server-side search filter after selection. MUI v7 does not
            // reliably fire onInputChange('reset') for uncontrolled inputValue, so we do it here.
            onInputChange?.(null as any, '', 'reset');
            onValueChange?.(newValue);
          }}
          onInputChange={onInputChange}
          {...other}
        />
      )}
    />
  );
}
